/**
 * Sohbetin yürütülmesi: mesaj gönderme, akışı okuma, araç sinyallerini uygulama.
 *
 * Panelin çiziminden ayrı tutuluyor çünkü akış panel kapanınca da sürmeli: kullanıcı
 * uzun bir yanıt beklerken paneli kapatıp işine dönebiliyor, döndüğünde yanıt orada.
 */
import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useOpenObjectScreen } from '@/features/shell/tabs/useOpenObjectScreen';
import { openReportTab } from '@/features/reports';
import { toast } from '@/shared/ui/toastStore';
import type { AiAttachmentRef } from '@/shared/types/aiChat';
import { aiKeys } from '../api/aiApi';
import { streamChat, type AiOpenScreenSignal, type AiStreamEvent } from '../api/aiStream';
import { newMessageId, useAiStore } from './aiStore';

export function useAiChat() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const { open: openObjectScreen } = useOpenObjectScreen();
  const abortRef = useRef<AbortController | null>(null);

  /** AI "şu ekranı aç" dediğinde: nesne ekranı ya da rapor sekmesi. */
  const applyOpenScreen = useCallback(
    (signal: AiOpenScreenSignal) => {
      if (!signal.id) return;
      if (signal.type === 'report') {
        openReportTab({
          reportActionId: signal.id,
          filterValue: signal.filterValue ?? '',
          reportName: signal.caption || t('ai.reportTab'),
        });
        return;
      }
      /*
       * Ekran açma NESNE kimliğiyle çalışıyor: AI ekran ya da nesne kimliği verebiliyor
       * ve ikisi de menüde aranıyor. Menüde karşılığı yoksa (yetki yok, ekran silinmiş)
       * sessizce geçmek yerine kullanıcıya söyleniyor — AI "açtım" demiş oluyor.
       */
      if (!openObjectScreen(signal.id)) {
        toast.info(t('ai.screenNotFound'), signal.caption ?? undefined);
      }
    },
    [openObjectScreen, t],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    useAiStore.getState().setStreaming(false);
  }, []);

  const send = useCallback(
    async (prompt: string, attachments: AiAttachmentRef[] = []) => {
      const text = prompt.trim();
      const store = useAiStore.getState();
      if (!text || store.streaming) return;
      if (store.quotaExceeded) {
        toast.warning(t('ai.quotaExceeded'));
        return;
      }

      const userMessageId = newMessageId();
      const assistantId = newMessageId();
      store.addMessage({
        id: userMessageId,
        role: 'user',
        content: text,
        attachments: attachments.length ? attachments : undefined,
      });
      store.addMessage({ id: assistantId, role: 'assistant', content: '', isStreaming: true });
      store.setPendingAttachments([]);
      store.setStreaming(true);

      const isNewSession = !store.sessionId;
      const controller = new AbortController();
      abortRef.current = controller;

      let sawText = false;
      const onEvent = (event: AiStreamEvent) => {
        const s = useAiStore.getState();
        switch (event.kind) {
          case 'session':
            s.setSession(event.sessionId);
            break;
          case 'conversation':
            // Sunucunun açtığı/çözdüğü sohbet kaydı: sonraki mesajlar aynı kayda yazılsın.
            s.setConversation(event.conversationId);
            break;
          case 'text':
            sawText = true;
            s.appendText(assistantId, event.text);
            break;
          case 'result':
            /*
             * CLI'ın `result` olayı son yanıt metnini bir kez daha taşıyor. Önceden bu da
             * metin gibi ekleniyordu ve aynı paragraf balonda iki kez görünüyordu. Metin
             * aktıysa yok sayılır; hiç akmadıysa (yalnız araç çalıştı) yanıt budur.
             */
            if (!sawText && event.text) {
              sawText = true;
              s.appendText(assistantId, event.text);
            }
            break;
          case 'tool':
            s.setActiveTool(event.tool);
            break;
          case 'artifact':
            s.addArtifact(assistantId, event.artifact);
            break;
          case 'openScreen':
            applyOpenScreen(event.target);
            break;
          case 'error':
            if (event.code === 'quota_exceeded') {
              s.setQuota({ ...(s.quota ?? emptyQuota()), remaining: 0, unlimited: false });
              s.updateMessage(assistantId, { content: t('ai.quotaExceededLong'), error: true });
            } else {
              s.updateMessage(assistantId, {
                content: `${s.messages.find((m) => m.id === assistantId)?.content ?? ''}\n\n**${t('ai.error')}:** ${event.message}`,
                error: true,
              });
            }
            break;
          case 'complete':
            if (event.quota) s.setQuota(event.quota);
            break;
        }
      };

      try {
        await streamChat({
          request: {
            SessionId: store.sessionId,
            Message: text,
            UserId: userId,
            ConversationId: store.conversationId,
            Attachments: attachments.length ? attachments : null,
            DataSourceCode: store.dataSourceCode,
          },
          isNewSession,
          signal: controller.signal,
          onEvent,
        });
      } finally {
        const s = useAiStore.getState();
        const message = s.messages.find((m) => m.id === assistantId);
        // Hiç metin gelmediyse baloncuk boş kalmasın: durduruldu mu, yanıtsız mı belli olsun.
        if (!sawText && !message?.content) {
          s.updateMessage(assistantId, {
            content: controller.signal.aborted ? t('ai.stopped') : t('ai.noAnswer'),
          });
        }
        s.updateMessage(assistantId, { isStreaming: false });
        s.setStreaming(false);
        s.setActiveTool(null);
        abortRef.current = null;
        // Sohbet listesi ve kota tazelensin (yeni sohbet başlığı sunucuda üretiliyor).
        if (userId) {
          void qc.invalidateQueries({ queryKey: aiKeys.conversations(userId) });
          void qc.invalidateQueries({ queryKey: aiKeys.quota(userId) });
        }
      }
    },
    [applyOpenScreen, qc, t, userId],
  );

  return { send, stop };
}

function emptyQuota() {
  return {
    limit: 0,
    used: 0,
    remaining: 0,
    unlimited: false,
    costUsed: 0,
    costLimitUsd: null,
    resetPeriod: '',
    periodEnd: null,
  };
}
